import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ContextMenu from '../components/ContextMenu';

const Students = () => {
  const [students, setStudents] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [metadata, setMetadata] = useState({ area: [], school: [], class: [], subject: [] });
  const [showModal, setShowModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    parentName: '',
    area: '',
    address: '',
    contactNumber: '',
    contactNumber2: '',
    school: '',
    schoolBranch: '',
    class: '',
    courseExam: '',
    subject: '',
    dateOfClassStart: '',
    timing: '',
    tutor: '',
    tutorMobileNumber: '',
    feesPerMonth: '',
    tutorFeesPerMonth: '',
    collectionType: 'End of Month',
    commission: 0,
    remarks: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchStudents();
    fetchTutors();
    fetchMetadata();
  }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/student-finals');
      setStudents(response.data.students || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTutors = async () => {
    try {
      const response = await axios.get('/api/tutor-finals');
      setTutors(response.data.tutors || []);
    } catch (error) {
      console.error('Error fetching tutors:', error);
    }
  };

  const fetchMetadata = async () => {
    try {
      const response = await axios.get('/api/metadata');
      setMetadata(response.data);
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      if (editingStudent) {
        await axios.put(`/api/student-finals/${editingStudent._id}`, formData);
        setMessage('Student updated successfully!');
      } else {
        await axios.post('/api/student-finals', formData);
        setMessage('Student added successfully!');
      }

      resetForm();
      fetchStudents();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving student:', error);
      setMessage(error.response?.data?.message || 'Error saving student');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setFormData({
      ...student,
      dateOfClassStart: student.dateOfClassStart?.split('T')[0] || '',
      tutor: student.tutor?._id || student.tutor
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this student?')) return;

    try {
      await axios.delete(`/api/student-finals/${id}`);
      setMessage('Student deleted successfully!');
      fetchStudents();
    } catch (error) {
      console.error('Error deleting student:', error);
      setMessage('Error deleting student');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      parentName: '',
      area: '',
      address: '',
      contactNumber: '',
      contactNumber2: '',
      school: '',
      schoolBranch: '',
      class: '',
      courseExam: '',
      subject: '',
      dateOfClassStart: '',
      timing: '',
      tutor: '',
      tutorMobileNumber: '',
      feesPerMonth: '',
      tutorFeesPerMonth: '',
      collectionType: 'End of Month',
      commission: 0,
      remarks: ''
    });
    setEditingStudent(null);
  };

  return (
    <div className="container" style={{ padding: '32px 24px' }}>
      <div className="page-header">
        <h1>Finalized Students</h1>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          + Add New Student
        </button>
      </div>

      {message && (
        <div className={`alert ${message.includes('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}
        </div>
      )}

      <div className="card">
        <h3>Students List ({students.length})</h3>
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>School</th>
                  <th>Class</th>
                  <th>Subject</th>
                  <th>Tutor</th>
                  <th>Fees/Month</th>
                  <th>Start Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {students.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '40px' }}>No students found</td>
                  </tr>
                ) : (
                  students.map((student) => (
                    <tr key={student._id}>
                      <td>{student.name}</td>
                      <td>{student.contactNumber}</td>
                      <td>{student.school}</td>
                      <td>{student.class}</td>
                      <td>{student.subject}</td>
                      <td>{student.tutor?.name || student.tutorName}</td>
                      <td>₹{student.feesPerMonth}</td>
                      <td>{new Date(student.dateOfClassStart).toLocaleDateString()}</td>
                      <td className="actions">
                        <ContextMenu
                          onEdit={() => handleEdit(student)}
                          onDelete={() => handleDelete(student._id)}
                          itemName={student.name}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <h2>{editingStudent ? 'Edit' : 'Add'} Student</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Parent Name</label>
                  <input type="text" name="parentName" value={formData.parentName} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Area *</label>
                  <input type="text" name="area" value={formData.area} onChange={handleChange} list="area-list" required />
                  <datalist id="area-list">
                    {metadata.area?.map((a) => (<option key={a._id} value={a.value} />))}
                  </datalist>
                </div>
                <div className="form-group">
                  <label>Contact Number *</label>
                  <input type="text" name="contactNumber" value={formData.contactNumber} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Contact Number 2</label>
                  <input type="text" name="contactNumber2" value={formData.contactNumber2} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>School/College *</label>
                  <input type="text" name="school" value={formData.school} onChange={handleChange} list="school-list" required />
                  <datalist id="school-list">
                    {metadata.school?.map((s) => (<option key={s._id} value={s.value} />))}
                  </datalist>
                </div>
                <div className="form-group">
                  <label>School Branch</label>
                  <input type="text" name="schoolBranch" value={formData.schoolBranch} onChange={handleChange} placeholder="e.g., Dharampeth, Sadar" />
                </div>
                <div className="form-group">
                  <label>Class/Stream *</label>
                  <input type="text" name="class" value={formData.class} onChange={handleChange} list="class-list" required />
                  <datalist id="class-list">
                    {metadata.class?.map((c) => (<option key={c._id} value={c.value} />))}
                  </datalist>
                </div>
                <div className="form-group">
                  <label>Course/Exam</label>
                  <input type="text" name="courseExam" value={formData.courseExam} onChange={handleChange} placeholder="e.g., JEE, NEET, CET" />
                </div>
                <div className="form-group">
                  <label>Subject *</label>
                  <input type="text" name="subject" value={formData.subject} onChange={handleChange} list="subject-list" required />
                  <datalist id="subject-list">
                    {metadata.subject?.map((s) => (<option key={s._id} value={s.value} />))}
                  </datalist>
                </div>
                <div className="form-group">
                  <label>Tutor *</label>
                  <select name="tutor" value={formData.tutor} onChange={handleChange} required>
                    <option value="">Select Tutor</option>
                    {tutors.map((tutor) => (
                      <option key={tutor._id} value={tutor._id}>{tutor.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tutor Mobile Number</label>
                  <input type="text" name="tutorMobileNumber" value={formData.tutorMobileNumber} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Date of Class Start *</label>
                  <input type="date" name="dateOfClassStart" value={formData.dateOfClassStart} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Timing *</label>
                  <input type="text" name="timing" value={formData.timing} onChange={handleChange} placeholder="e.g., 4:00 PM - 5:00 PM" required />
                </div>
                <div className="form-group">
                  <label>Fees/Month (Student Pays) *</label>
                  <input type="number" name="feesPerMonth" value={formData.feesPerMonth} onChange={handleChange} min="0" required />
                </div>
                <div className="form-group">
                  <label>Tutor Fees/Month</label>
                  <input type="number" name="tutorFeesPerMonth" value={formData.tutorFeesPerMonth} onChange={handleChange} min="0" />
                </div>
                <div className="form-group">
                  <label>Collection Type</label>
                  <select name="collectionType" value={formData.collectionType} onChange={handleChange}>
                    <option value="Advance">Advance</option>
                    <option value="End of Month">End of Month</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Commission</label>
                  <input type="number" name="commission" value={formData.commission} onChange={handleChange} min="0" />
                </div>
              </div>
              <div className="form-group">
                <label>Address</label>
                <textarea name="address" value={formData.address} onChange={handleChange} rows="2" />
              </div>
              <div className="form-group">
                <label>Remarks</label>
                <textarea name="remarks" value={formData.remarks} onChange={handleChange} rows="2" placeholder="Any additional notes" />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Students;
